import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageSquare,
  Loader2,
  CheckCircle,
  XCircle,
  Bot,
  Send,
  Check,
  X,
  Users,
  User
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Client, Trip, TripComponent } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { buildTripIntro } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserAvatar } from '@/components/UserAvatar';
import { useAuth } from '@/context/AuthContext';

interface TripAssistantColumnProps {
  trip: Trip;
  components: TripComponent[];
  contacts?: Client[];
  onSuccess?: () => void;
  onTripUpdated?: () => void;
  isExpanded?: boolean;
  onAcceptAll?: () => void;
  hideInput?: boolean;
  externalMessageEventName?: string; // when provided, listen for events { tripId, text }
}

interface Message {
  role: "system" | "user" | "assistant";
  content: string;
}

interface TripUpdate {
  name?: string;
  startDate?: string;
  endDate?: string;
  countries?: string[];
  clientName?: string;
  clientInterests?: string[];
  description?: string;
}

interface SuggestedChange {
  id?: number;
  action: "add" | "update" | "delete" | "trip_update";
  component?: TripComponent;
  originalComponent?: TripComponent;
  tripUpdate?: TripUpdate;
  status: "pending" | "accepted" | "rejected";
}

export default function TripAssistantColumn({
  trip,
  components,
  contacts,
  onSuccess,
  onTripUpdated,
  isExpanded,
  onAcceptAll,
  hideInput,
  externalMessageEventName
}: TripAssistantColumnProps) {
  const { toast } = useToast();
  const { user } = useAuth?.() || { user: null } as any;
  const [userMessage, setUserMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [suggestedChanges, setSuggestedChanges] = useState<SuggestedChange[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Load saved conversation from localStorage when component mounts
  useEffect(() => {
    const tripId = trip.id;
    const savedMessages = localStorage.getItem(`trip_assistant_messages_${tripId}`);
    const savedChanges = localStorage.getItem(`trip_assistant_changes_${tripId}`);
    
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (error) {
        console.error("Error parsing saved messages:", error);
      }
    } else {
      // Initialize with default messages if no saved conversation
      const initialSystemMessage: Message = {
        role: "system",
        content: "I'm your trip assistant. I can help you modify your itinerary based on your preferences."
      };

      // Add a welcome message from the assistant
      const welcomeMessage: Message = {
        role: "assistant",
        content: `I'm ready to help with your trip to ${buildTripIntro(trip)}.
        
How would you like to modify your itinerary? You can ask me to:
- Add new activities or attractions to specific days
- Replace certain days with different destinations
- Remove parts of your itinerary
- Reorganize your schedule
- Provide recommendations for specific interests
        
Just let me know what changes you'd like to make.`
      };

      setMessages([initialSystemMessage, welcomeMessage]);
    }
    
    if (savedChanges) {
      try {
        setSuggestedChanges(JSON.parse(savedChanges));
      } catch (error) {
        console.error("Error parsing saved changes:", error);
        setSuggestedChanges([]);
      }
    } else {
      setSuggestedChanges([]);
    }
  }, [trip.id, trip.name, trip.countries, trip.startDate, trip.endDate]);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(`trip_assistant_messages_${trip.id}`, JSON.stringify(messages));
    }
  }, [messages, trip.id]);
  
  // Save suggested changes to localStorage when they change
  useEffect(() => {
    if (suggestedChanges.length > 0) {
      localStorage.setItem(`trip_assistant_changes_${trip.id}`, JSON.stringify(suggestedChanges));
    }
  }, [suggestedChanges, trip.id]);

  // Scroll to bottom of messages within the container only
  useEffect(() => {
    if (messagesEndRef.current) {
      // Use scrollIntoView but prevent the container from affecting page scroll
      const scrollContainer = messagesEndRef.current.closest('.scrollarea-viewport');
      if (scrollContainer) {
        // Only scroll the container, not the whole page
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Handle sending a message to the AI
  const handleSendMessage = async (overrideText?: string) => {
    const text = overrideText !== undefined ? overrideText : userMessage;
    if (!text.trim() || loading) return;
    
    // Add user message to chat
    const newUserMessage: Message = { role: "user", content: text };
    setMessages(prev => [...prev, newUserMessage]);
    setUserMessage("");
    setLoading(true);
    
    try {      
      // Create a context object with the trip details and all components
      const tripContext = {
        trip: {
          ...trip,
          startDate: trip.startDate ? new Date(trip.startDate).toISOString() : null,
          endDate: trip.endDate ? new Date(trip.endDate).toISOString() : null
        },
        components: components.map(comp => ({
          ...comp,
          startDate: comp.startDate ? new Date(comp.startDate).toISOString() : null,
          endDate: comp.endDate ? new Date(comp.endDate).toISOString() : null
        }))
      };
      
      // Send the message to the backend
      const response = await apiRequest("POST", `/api/trips/${trip.id}/assistant`, {
        userMessage: text,
        chatHistory: messages.filter(m => m.role !== "system"),
        tripContext
      });
      
      if (!response) {
        throw new Error("Failed to get response from assistant");
      }
      
      // Clean up the message to remove any JSON code blocks
      let cleanedMessage = response.message;
      
      // Process the message to remove different code formats
      
      // 1. Remove XML style suggested_changes blocks
      cleanedMessage = cleanedMessage.replace(/<suggested_changes>[\s\S]*?<\/suggested_changes>/g, '');
      
      // 2. Remove all markdown code blocks with JSON or any language
      cleanedMessage = cleanedMessage.replace(/```(?:json)?[\s\S]*?```/g, '');
      
      // 3. Remove any standalone JSON array starts for action components
      const jsonArrayStarts = cleanedMessage.match(/\[\s*\{\s*"action":/g);
      if (jsonArrayStarts) {
        for (const match of jsonArrayStarts) {
          // Find the start position
          const startPos = cleanedMessage.indexOf(match);
          if (startPos >= 0) {
            // Try to find the matching end bracket by counting brackets
            let bracketCount = 0;
            let endPos = startPos;
            let inString = false;
            
            for (let i = startPos; i < cleanedMessage.length; i++) {
              const char = cleanedMessage[i];
              
              // Handle string boundaries
              if (char === '"' && cleanedMessage[i-1] !== '\\') {
                inString = !inString;
                continue;
              }
              
              // Only count brackets outside of strings
              if (!inString) {
                if (char === '[') bracketCount++;
                else if (char === ']') {
                  bracketCount--;
                  if (bracketCount === 0) {
                    endPos = i + 1;
                    break;
                  }
                }
              }
            }
            
            if (endPos > startPos) {
              // Remove the JSON array
              cleanedMessage = cleanedMessage.substring(0, startPos) + 
                "\n\nI've included some specific changes to your itinerary below." + 
                cleanedMessage.substring(endPos);
            }
          }
        }
      }
      
      // 4. Clean up any extra newlines that might be left after removing blocks
      cleanedMessage = cleanedMessage.replace(/\n\s*\n\s*\n/g, '\n\n');
      
      // 5. Final trim
      cleanedMessage = cleanedMessage.trim();
      
      // Add the assistant's response to the chat
      const assistantMessage: Message = { 
        role: "assistant", 
        content: cleanedMessage 
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // If the assistant suggested changes, add them to the suggestedChanges state
      // Process suggested changes if any
      if (response.suggestedChanges && response.suggestedChanges.length > 0) {
        // Check if there's a trip update in the response
        if (response.tripUpdate) {
          // Create a special "trip_update" change if there's a trip update
          const tripUpdateChange: SuggestedChange = {
            action: "trip_update",
            tripUpdate: response.tripUpdate,
            status: "pending"
          };
          
          // Set the trip update as the only suggested change
          setSuggestedChanges([tripUpdateChange]);
        } else {
          // Process component changes normally
          const rawChanges: SuggestedChange[] = (response.suggestedChanges || []).map((c: any) => ({
            action: c.action,
            component: c.component,
            originalComponent: c.originalComponent,
            status: 'pending'
          }));

          // Coalesce multiple 'add' changes for the same logical component (e.g., hotel spanning days)
          const coalesced: SuggestedChange[] = [];
          for (const ch of rawChanges) {
            if (ch.action === 'add' && ch.component) {
              const title = (ch.component as any).title || '';
              const key = `${(ch.component as any).type || ''}::${title}`;
              const existingIndex = coalesced.findIndex(x => x.action === 'add' && x.component && (((x.component as any).type || '') + '::' + (((x.component as any).title) || '')) === key);
              if (existingIndex >= 0) {
                // Merge spans/dates if present
                const existing = coalesced[existingIndex];
                const startA = (existing.component as any)?.startDate || (existing.component as any)?.date;
                const endA = (existing.component as any)?.endDate || startA;
                const startB = (ch.component as any)?.startDate || (ch.component as any)?.date;
                const endB = (ch.component as any)?.endDate || startB;
                const minStart = [startA, startB].filter(Boolean).sort()[0];
                const maxEnd = [endA, endB].filter(Boolean).sort().slice(-1)[0];
                (existing.component as any).startDate = minStart || (existing.component as any).startDate;
                (existing.component as any).endDate = maxEnd || (existing.component as any).endDate;
              } else {
                coalesced.push(ch);
              }
            } else {
              coalesced.push(ch);
            }
          }

          setSuggestedChanges(coalesced);
        }
      }
      
      // Process trip updates if any
      if (response.updatedTrip) {
        // Show success toast for trip updates
        toast({
          title: "Trip Updated",
          description: "Trip details have been successfully updated.",
        });
        
        // If onTripUpdated callback is provided, call it to refresh parent's trip data
        if (onTripUpdated) {
          onTripUpdated();
        }
        
        // Also invalidate the trip query to make sure it's refreshed
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}`] });
      }
    } catch (error) {
      console.error("Error sending message to assistant:", error);
      
      // Add an error message to the chat
      const errorMessage: Message = {
        role: "assistant",
        content: "I'm sorry, I encountered an error while processing your request. Please try again."
      };
      
      setMessages(prev => [...prev, errorMessage]);
      
      toast({
        title: "Error",
        description: "Failed to communicate with the assistant",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Listen for external command bar messages to this trip
  useEffect(() => {
    const eventName = externalMessageEventName ?? 'tripAssistantMessage';
    const handler = (e: any) => {
      try {
        const detail = e?.detail || {};
        if (detail.tripId === trip.id && typeof detail.text === 'string') {
          handleSendMessage(detail.text);
        }
      } catch {}
    };
    window.addEventListener(eventName, handler as EventListener);
    return () => window.removeEventListener(eventName, handler as EventListener);
  }, [trip.id, externalMessageEventName]);

  // Helper function to check if all changes have been processed and add a summary if needed
  const checkForAllChangesProcessed = (updatedChanges: SuggestedChange[]) => {
    // If there are no more pending changes, add a summary
    const pendingChanges = updatedChanges.filter(change => change.status === "pending");
    if (pendingChanges.length === 0) {
      const acceptedCount = updatedChanges.filter(change => change.status === "accepted").length;
      const rejectedCount = updatedChanges.filter(change => change.status === "rejected").length;
      const totalCount = acceptedCount + rejectedCount;
      
      const summaryMessage: Message = {
        role: "assistant",
        content: `I've processed all your suggested changes: ${acceptedCount} of ${totalCount} were applied${rejectedCount > 0 ? ` and ${rejectedCount} were rejected` : ""}.`
      };
      
      setMessages(prev => [...prev, summaryMessage]);
      // Auto-close suggestions by clearing list
      setSuggestedChanges([]);
    }
  };

  // Handle accepting a suggested change
  const handleAcceptChange = async (index: number) => {
    const change = suggestedChanges[index];
    if (!change) return;
    
    try {
      let result;
      
      // Perform the appropriate API call based on the action
      if (change.action === "trip_update" && change.tripUpdate) {
        // This is a trip update (date change, name change, etc.)
        const tripUpdateData: any = {};
        
        // Only include fields that have values in the update
        if (change.tripUpdate.name) {
          tripUpdateData.name = change.tripUpdate.name;
        }
        
        if (change.tripUpdate.startDate) {
          tripUpdateData.startDate = new Date(change.tripUpdate.startDate);
        }
        
        if (change.tripUpdate.endDate) {
          tripUpdateData.endDate = new Date(change.tripUpdate.endDate);
        }
        
        if (change.tripUpdate.countries) {
          tripUpdateData.countries = change.tripUpdate.countries;
        }
        
        if (change.tripUpdate.clientName) {
          tripUpdateData.clientName = change.tripUpdate.clientName;
        }
        
        if (change.tripUpdate.clientInterests) {
          tripUpdateData.clientInterests = change.tripUpdate.clientInterests;
        }
        
        if (change.tripUpdate.description) {
          tripUpdateData.description = change.tripUpdate.description;
        }
        
        // Send the update to the server
        result = await apiRequest("PATCH", `/api/trips/${trip.id}`, tripUpdateData);
        
        // Update the trip data
        if (onTripUpdated) {
          onTripUpdated();
        }
        
        // Invalidate the trip query to refresh data
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}`] });
        
        // Also invalidate components to reflect any date shifts
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}/components`] });
        
        // Invalidate day destinations to reflect any date shifts
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}/day-destinations`] });
      } else if (change.action === "add" && change.component) {
        // Normalize the component data
        const componentToAdd = {
          ...change.component,
          tripId: trip.id,
        };
        
        result = await apiRequest("POST", "/api/components", componentToAdd);
      } else if (change.action === "update" && change.originalComponent && change.component) {
        // Normalize the component data
        const componentToUpdate = {
          ...change.component,
          tripId: trip.id,
        };
        
        result = await apiRequest(
          "PATCH", 
          `/api/components/${change.originalComponent.id}`, 
          componentToUpdate
        );
      } else if (change.action === "delete") {
        // For delete actions, we need to check both component.id and originalComponent.id
        const componentId = change.originalComponent?.id || change.component?.id;
        if (!componentId) {
          throw new Error("Cannot delete component: No component ID found");
        }
        result = await apiRequest("DELETE", `/api/components/${componentId}`);
      }
      
      // Update the status of the change
      const updatedChanges = [...suggestedChanges];
      updatedChanges[index] = {
        ...change,
        status: "accepted",
        id: result?.id
      };
      
      setSuggestedChanges(updatedChanges);
      
      // Check if all changes have been processed
      checkForAllChangesProcessed(updatedChanges);
      
      // Update the trip components if it was a component change
      if (change.action !== "trip_update") {
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}/components`] });
      }
      
      if (onSuccess) {
        onSuccess();
      }
      
      toast({
        title: "Success",
        description: change.action === "trip_update" 
          ? "Trip details updated successfully" 
          : `${change.action === "add" ? "Added" : change.action === "update" ? "Updated" : "Deleted"} component successfully`,
      });
    } catch (error) {
      console.error(`Error processing ${change.action} action:`, error);
      
      // Update the status of the change to show the error
      const updatedChanges = [...suggestedChanges];
      updatedChanges[index] = {
        ...change,
        status: "rejected"
      };
      
      setSuggestedChanges(updatedChanges);
      
      // Check if all changes have been processed
      checkForAllChangesProcessed(updatedChanges);
      
      toast({
        title: "Error",
        description: change.action === "trip_update" 
          ? "Failed to update trip details" 
          : `Failed to ${change.action} component`,
        variant: "destructive"
      });
    }
  };

  // Handle rejecting a suggested change
  const handleRejectChange = (index: number) => {
    const updatedChanges = [...suggestedChanges];
    updatedChanges[index] = {
      ...suggestedChanges[index],
      status: "rejected"
    };
    
    setSuggestedChanges(updatedChanges);
    
    // Check if all changes have been processed
    checkForAllChangesProcessed(updatedChanges);
  };
  
  // Handle accepting all pending changes
  const handleAcceptAllChanges = async () => {
    // Count pending changes
    const pendingChanges = suggestedChanges.filter(change => change.status === "pending");
    if (pendingChanges.length === 0) return;
    
    let successCount = 0;
    let failureCount = 0;
    
    // Create a new array to collect all the updated changes
    const allChanges = [...suggestedChanges];
    
    // Process each pending change
    for (const change of pendingChanges) {
      const index = suggestedChanges.findIndex(c => c === change);
      try {
        let result;
        
        // Perform the appropriate API call based on the action
        if (change.action === "trip_update" && change.tripUpdate) {
          // This is a trip update (date change, name change, etc.)
          const tripUpdateData: any = {};
          
          // Only include fields that have values in the update
          if (change.tripUpdate.name) {
            tripUpdateData.name = change.tripUpdate.name;
          }
          
          if (change.tripUpdate.startDate) {
            tripUpdateData.startDate = new Date(change.tripUpdate.startDate);
          }
          
          if (change.tripUpdate.endDate) {
            tripUpdateData.endDate = new Date(change.tripUpdate.endDate);
          }
          
          if (change.tripUpdate.countries) {
            tripUpdateData.countries = change.tripUpdate.countries;
          }
          
          if (change.tripUpdate.clientName) {
            tripUpdateData.clientName = change.tripUpdate.clientName;
          }
          
          if (change.tripUpdate.clientInterests) {
            tripUpdateData.clientInterests = change.tripUpdate.clientInterests;
          }
          
          if (change.tripUpdate.description) {
            tripUpdateData.description = change.tripUpdate.description;
          }
          
          // Send the update to the server
          result = await apiRequest("PATCH", `/api/trips/${trip.id}`, tripUpdateData);
          
          // Update the trip data
          if (onTripUpdated) {
            onTripUpdated();
          }
          
          // Invalidate the trip query to refresh data
          queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}`] });
          
          // Also invalidate components to reflect any date shifts
          queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}/components`] });
          
          // Invalidate day destinations to reflect any date shifts
          queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}/day-destinations`] });
        } else if (change.action === "add" && change.component) {
          // Normalize the component data
          const componentToAdd = {
            ...change.component,
            tripId: trip.id,
          };
          
          result = await apiRequest("POST", "/api/components", componentToAdd);
        } else if (change.action === "update" && change.originalComponent && change.component) {
          // Normalize the component data
          const componentToUpdate = {
            ...change.component,
            tripId: trip.id,
          };
          
          result = await apiRequest(
            "PATCH", 
            `/api/components/${change.originalComponent.id}`, 
            componentToUpdate
          );
        } else if (change.action === "delete") {
          // For delete actions, we need to check both component.id and originalComponent.id
          const componentId = change.originalComponent?.id || change.component?.id;
          if (!componentId) {
            throw new Error("Cannot delete component: No component ID found");
          }
          result = await apiRequest("DELETE", `/api/components/${componentId}`);
        }
        
        // Mark as accepted in our local array
        allChanges[index] = {
          ...change,
          status: "accepted",
          id: result?.id
        };
        
        successCount++;
      } catch (error) {
        console.error(`Error ${change.action}ing component:`, error);
        
        // Mark as rejected in our local array
        allChanges[index] = {
          ...change,
          status: "rejected"
        };
        
        failureCount++;
      }
    }
    
    // Update the state once with all changes
    setSuggestedChanges(allChanges);
    
    // Update the trip components
    queryClient.invalidateQueries({ queryKey: [`/api/trips/${trip.id}/components`] });
    
    // Call onSuccess if provided
    if (onSuccess) {
      onSuccess();
    }
    
    // Add a summary message to the chat
    const totalChanges = pendingChanges.length;
    const summaryMessage: Message = {
      role: "assistant",
      content: `I've processed your requested changes: ${successCount} of ${totalChanges} suggestions were successfully applied${failureCount > 0 ? `, and ${failureCount} could not be applied` : ""}.`
    };
    setMessages(prev => [...prev, summaryMessage]);
    
    // Show a toast with the results
    if (successCount > 0) {
      toast({
        title: `${successCount} changes applied`,
        description: failureCount > 0 ? `${failureCount} changes failed` : "All changes were successful",
        variant: failureCount > 0 ? "destructive" : "default"
      });
    }
  };
  
  // Handle rejecting all pending changes
  const handleRejectAllChanges = () => {
    // Count pending changes
    const pendingChanges = suggestedChanges.filter(change => change.status === "pending");
    if (pendingChanges.length === 0) return;
    
    // Update all pending changes to rejected
    const updatedChanges = suggestedChanges.map(change => 
      change.status === "pending" ? { ...change, status: "rejected" as const } : change
    );
    
    setSuggestedChanges(updatedChanges);
    
    // Add a summary message to the chat
    const totalChanges = pendingChanges.length;
    const summaryMessage: Message = {
      role: "assistant",
      content: `I've noted that all ${totalChanges} suggested changes were rejected. Your itinerary remains unchanged.`
    };
    setMessages(prev => [...prev, summaryMessage]);
    
    toast({
      title: "All changes rejected",
      description: "No changes were applied to your itinerary",
      variant: "default"
    });
  };

  // Get message display class
  const getMessageClass = (role: string) => {
    switch (role) {
      case "user":
        return "bg-primary/10 ml-auto";
      case "assistant":
        return "bg-muted";
      default:
        return "";
    }
  };
  
  // Check if there are any changes (including accepted/rejected ones)
  const hasChanges = suggestedChanges.length > 0;
  const hasPendingChanges = suggestedChanges.some(change => change.status === "pending");

  return (
    <div className="flex flex-col h-full">
      {/* Chat message area */}
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-4">
          {messages.filter(m => m.role !== "system").map((message, i) => (
            <div key={i} className={`relative mb-2 ${message.role === 'user' ? 'ml-[15%]' : ''}`}>
              <div className="flex items-start gap-3">
                {message.role === 'user' && (
                  user?.email ? (
                    <UserAvatar email={user.email} name={user.displayName || 'User'} size={32} />
                  ) : (
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-orange-100 text-orange-700 text-xs">U</AvatarFallback>
                    </Avatar>
                  )
                )}
                <div className={`flex-1 ${message.role === 'user' ? '' : ''}`}>
                  <div className={`${message.role === 'assistant' ? 'bg-white' : 'bg-gray-100'} rounded-lg p-3 border border-gray-200`}>
                    <div className="prose max-w-none break-words overflow-wrap-anywhere">
                      <p className="whitespace-pre-line text-sm">{message.content}</p>
                    </div>
                    {message.role === 'assistant' && (
                      <button
                        className="absolute bottom-2 right-2 p-2 rounded hover:bg-gray-100"
                        title="Copy response"
                        onClick={() => navigator.clipboard.writeText(message.content)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gray-500">
                          <path d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="relative mb-2 bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                <span className="text-sm text-gray-500">AIVIA is thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      
      {/* Inline suggested changes hidden (handled by modal) */}
      {hasChanges && (
        <div className="hidden" />
      )}
      
      {/* Message input (hidden when using unified Command Bar) */}
      {!hideInput && (
        <div className="p-3 border-t">
          <div className="flex gap-2">
            <Textarea
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              placeholder="Type your message..."
              className="min-h-[60px] resize-none"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Button
              className="self-end"
              onClick={() => handleSendMessage()}
              disabled={!userMessage.trim() || loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              <span className="sr-only">Send</span>
            </Button>
          </div>
        </div>
      )}

      {/* Suggested Changes Modal */}
      <Dialog open={hasPendingChanges}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Suggested Changes</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between mb-2">
            {hasPendingChanges && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleAcceptAllChanges}><Check className="h-4 w-4 mr-1" /> Accept All</Button>
                <Button variant="outline" size="sm" onClick={handleRejectAllChanges}><X className="h-4 w-4 mr-1" /> Reject All</Button>
              </div>
            )}
          </div>
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {suggestedChanges.map((change, i) => (
              <div key={i} className={`rounded-md p-2 text-xs ${change.status === 'accepted' ? 'bg-green-50' : change.status === 'rejected' ? 'bg-red-50' : 'bg-background'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge>{change.action === 'trip_update' ? 'Update Trip' : change.action.charAt(0).toUpperCase() + change.action.slice(1)}</Badge>
                    {change.status !== 'pending' && (
                      <Badge variant="outline">{change.status === 'accepted' ? 'Accepted' : 'Rejected'}</Badge>
                    )}
                  </div>
                  {change.status === 'pending' && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" aria-label={`Accept change: ${change.action === 'trip_update' ? (change.tripUpdate?.description || 'Trip details change') : (change.component?.title || 'Change')}`} className="h-6 w-6" onClick={() => handleAcceptChange(i)}><CheckCircle className="h-4 w-4 text-green-500" /></Button>
                      <Button variant="ghost" size="icon" aria-label={`Reject change: ${change.action === 'trip_update' ? (change.tripUpdate?.description || 'Trip details change') : (change.component?.title || 'Change')}`} className="h-6 w-6" onClick={() => handleRejectChange(i)}><XCircle className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  )}
                </div>
                <div className="mt-1 text-xs">
                  {change.action === 'trip_update' ? (change.tripUpdate?.description || 'Trip details change') : (change.component?.title || 'Change')}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}